import { PathPlannerOperationMode } from "@ptx-autonomous/libs/api";
import type {
	Api,
	PlannedPathCollection,
	SimplePoint,
} from "@ptx-autonomous/libs/api-models";
import type { LineString, Polygon, Position } from "geojson";
import type { ImplementV2Model } from "@ptx-autonomous/libs/flightpath-service-api-models";
import {
	type PathPlannerOptions,
	type PathPlanningRequest,
	type PathPlanningResponse,
	type PassOrder,
	type GetPathOptions,
	type PlanPathResult,
	type ReferenceABLine,
	HEADLANDS_MAX_DEPTH,
	HEADLANDS_MIN_DEPTH,
	UNCAP_HEADLANDS_MAX_DEPTH,
	UNCAP_HEADLANDS_MIN_DEPTH,
	type LoggingInfo,
	type SteeringStateSpace,
	type ImplementWithCalibration,
} from "../types";
import { CancellationToken, interpolatePlannedPath } from "../utils";
import { createEstimatedCoverage } from "./estimated-coverage";
import { usePreMissionMetrics } from "./pre-mission-metrics";
import { Guid } from "typescript-guid";

const HASH_SAMPLE_INTERVAL = 10;
const COORDINATE_PRECISION = 6;

function pointsToPolygon(points: SimplePoint[]): Polygon {
	const polygon: Polygon = {
		type: "Polygon",
		coordinates: [points.map(point => [point.Long, point.Lat])],
	};
	polygon.coordinates[0].push(polygon.coordinates[0][0]);
	return polygon;
}

export async function getPath(
	http: Api<unknown>,
	field: Polygon,
	options: PathPlannerOptions,
	implement: ImplementV2Model,
	steeringStateSpace: SteeringStateSpace,
	tractorTurningRadiusMeters: number,
	startPoint?: Position,
	loggingInfo?: LoggingInfo,
	referenceABLine?: ReferenceABLine,
) {
	const fieldBoundary = field.coordinates[0].map(([lng, lat]: Position) => ({
		Lat: lat,
		Long: lng,
		Alt: 0,
	}));

	// Extract inner rings from field boundary as obstacles
	const obstacles = field.coordinates.slice(1);
	const obstaclesPoints: SimplePoint[][] = [];
	for (const obstacle of obstacles) {
		obstaclesPoints.push(
			obstacle.map(
				([lng, lat]: Position) =>
					({
						Lat: lat,
						Long: lng,
						Alt: 0,
					}) as SimplePoint,
			),
		);
	}

	const hitchToImplementAxle =
		implement.ConnectorReferencePointImplement?.ReferencePointX ?? 0;
	const rearAxleToHitch = -(
		implement.ConnectorReferencePointTractor?.ReferencePointX ?? 0
	);

	const input: PathPlanningRequest = {
		RowHeading: options.heading,
		Depth: options.depth,
		ImplementWidthMeters: options.implementWidthMeters,
		SwathExtensionLength: options.swathExtensionLength,
		TurningRadiusMeters: options.turningRadiusMeters,
		TractorTurningRadiusMeters: tractorTurningRadiusMeters,
		HitchToImplementAxle: hitchToImplementAxle,
		RearAxleToHitch: rearAxleToHitch,
		TurnPointDistance: 2.5,
		OperationMode:
			options.operationMode ??
			PathPlannerOperationMode.SingleMachineInsideFirst,
		FieldBoundary: fieldBoundary as SimplePoint[],
		Obstacles: obstaclesPoints,
		Overlap: options.overlap ?? 0,
		PassOrder: options.pattern,
		StartPoint: {
			Lat: startPoint?.[1] ?? 0,
			Long: startPoint?.[0] ?? 0,
			Alt: 0,
		} as SimplePoint,
		...(referenceABLine ? { ReferenceABLine: referenceABLine } : {}),
		HeadlandSearchIncrement: 1,
		HeadlandSizeSearch: "SearchOff", // Prevents the path planner from searching for a valid headland depth
		SteeringStateSpace: steeringStateSpace,
		...(loggingInfo ?? {}),
	};

	try {
		const response = await http.request<PathPlanningResponse>({
			path: "/path-planner",
			method: "POST",
			format: "json",
			body: input,
			cancelToken: "path-planner",
		});
		if (response.status !== 200) {
			console.error(await response.text());
			throw new Error("Failed to get path");
		}

		const data = response.data;

		const workAreas: Polygon[] = [];
		for (const boundary of data.Boundaries.InnerFieldBoundaries) {
			workAreas.push(pointsToPolygon(boundary));
		}

		return {
			path: data.Path as PlannedPathCollection[],
			clothoid: data.ParametricPath,
			workAreas,
			mitigationData:
				data.MitigationData.CorrectedTurns?.length > 0 ||
				data.MitigationData.ShortenedSegments?.length > 0
					? {
							correctedTurns:
								data.MitigationData.CorrectedTurns || [],
							shortenedSegments:
								data.MitigationData.ShortenedSegments || [],
						}
					: undefined,
			version: data.Version,
			requestId: data.requestID,
		};
	} catch {
		//
	}
}

function hashPath(pathPoints: SimplePoint[]): string {
	const sampledPoints = pathPoints.filter(
		(_, i) => i % HASH_SAMPLE_INTERVAL === 0,
	);
	const hash = sampledPoints
		.map(p => `${p.Lat.toFixed(6)},${p.Long.toFixed(COORDINATE_PRECISION)}`)
		.join("|");
	return hash;
}

function removeDuplicatePaths(pathResults: PlanPathResult[]): PlanPathResult[] {
	const seenHashes = new Set<string>();
	const uniquePaths: PlanPathResult[] = [];

	for (const result of pathResults) {
		if (!result.pathResult?.pathPoints) {
			uniquePaths.push(result);
			continue;
		}

		const pathHash = hashPath(result.pathResult.pathPoints);
		if (!seenHashes.has(pathHash)) {
			seenHashes.add(pathHash);
			uniquePaths.push(result);
		}
	}

	return uniquePaths;
}

export async function planPath(
	http: Api<unknown>,
	fieldBoundary: Polygon,
	pathOptions: GetPathOptions,
	implement: ImplementWithCalibration,
	startPoint: Position | undefined,
	targetSpeed: number,
	turningSpeed: number,
	cancellationToken: CancellationToken,
	progressCallback: () => void,
	steeringStateSpace: SteeringStateSpace,
	tractorTurningRadiusMeters: number,
	loggingInfo?: LoggingInfo,
	referenceABLine?: ReferenceABLine,
) {
	if (
		!implement?.Sections?.[0]?.ElementWorkingWidth ||
		!implement.ApplicationParameters
	) {
		throw new Error(
			"Implement is missing SectionWidths or Application Parameters",
		);
	}
	const sectionWidths = implement.Sections[0].ElementWorkingWidth;

	if (!implement.calibration?.TurningRadiusMeters) {
		throw new Error("Implement is missing calibration data");
	}

	const calibratedTurningRadiusMeters =
		implement.calibration.TurningRadiusMeters;
	let turningRadius = calibratedTurningRadiusMeters;
	if (turningRadius < tractorTurningRadiusMeters) {
		console.log(
			"Discarding calibrated minimum turning radius in favor of tractor minimum",
			{
				implementId: implement.ImplementId,
				implementName: implement.ImplementName,
				machineId: loggingInfo?.machineID,
				machineName: loggingInfo?.machineName,
				calibratedTurningRadiusMeters,
				tractorTurningRadiusMeters,
			},
		);
		turningRadius = tractorTurningRadiusMeters;
	}

	let maxDepth = HEADLANDS_MAX_DEPTH;
	let minDepth = HEADLANDS_MIN_DEPTH;

	if (pathOptions.uncapHeadlands) {
		maxDepth = UNCAP_HEADLANDS_MAX_DEPTH;
		minDepth = UNCAP_HEADLANDS_MIN_DEPTH;
	}

	let pathVariants: [PassOrder, boolean, number?][] = [];

	if (pathOptions.prefersConsecutiveOrder) {
		pathVariants = [["ConsecutiveOrder", !!pathOptions.startLocation]];
	} else {
		pathVariants = [
			["ConsecutiveOrder", !!pathOptions.startLocation],
			["MultiSkipOrder", !!pathOptions.startLocation],
			["SkipOrder", !!pathOptions.startLocation],
			["CustomOrder", !!pathOptions.startLocation, 3],
			["CustomOrder", !!pathOptions.startLocation, 4],
			["CustomOrder", !!pathOptions.startLocation, 5],
		];
	}

	const pathResultsSettled: PromiseSettledResult<PlanPathResult>[] =
		await Promise.allSettled(
			pathVariants.map(async ea => {
				const result: PlanPathResult = {
					passOrder: ea[0],
					startOption: ea[1] ? "user" : "optimal",
					skipPass: ea[2],
					pathResult: undefined,
					pathLengthOptimal: false,
					workAreaAreaOptimal: false,
					totalTimeOptimal: false,
					headlandDepthOptimal: false,
					efficiencyOptimal: false,
					requestId: "",
					version: "",
				};
				try {
					const depthValues = Array.from(
						{ length: Math.ceil(maxDepth - minDepth) + 1 },
						(_, i) => minDepth + i,
					);

					// Make request for depths 2 to 4 all at once in 0.5 increments
					const planPathResultsSettled = await Promise.allSettled(
						depthValues.map(async depth => {
							const pathResult = getPath(
								http,
								fieldBoundary,
								{
									heading: pathOptions.heading,
									depth,
									implementWidthMeters: sectionWidths,
									swathExtensionLength:
										pathOptions.swathExtension,
									turningRadiusMeters: turningRadius,
									overlap: pathOptions.overlap,
									pattern: result.passOrder,
									operationMode:
										PathPlannerOperationMode.SingleMachineTillageNoHeadland,
									skipPass: result.skipPass,
								},
								implement,
								steeringStateSpace,
								tractorTurningRadiusMeters,
								result.startOption === "user"
									? startPoint
									: undefined,
								loggingInfo
									? {
											...loggingInfo,
											requestID: Guid.create().toString(),
										}
									: undefined,
								referenceABLine,
							);

							// Use Promise.race and cancellationToken mechanism to allow the user to skip requests that take too long
							// Note: Abort controller does not work when a request is already in flight
							const pathResultRace = await Promise.race([
								pathResult,
								cancellationToken.cancellationPromise,
							])
								.then(response => {
									if (!response) {
										throw new Error("No response");
									}
									return response;
								})
								.finally(() => {
									if (progressCallback) {
										// Update progress after each request completes
										progressCallback();
									}
								});

							return { pathResult: pathResultRace, depth };
						}),
					);

					// Filter out the failed requests and keep the associated depth
					const planPathResults = planPathResultsSettled
						.filter(
							result =>
								result.status === "fulfilled" &&
								Boolean(result.value.pathResult),
						)
						.map(
							result =>
								(
									result as PromiseFulfilledResult<{
										pathResult: NonNullable<
											Awaited<ReturnType<typeof getPath>>
										>;
										depth: number;
									}>
								).value,
						);

					// Find the first valid path (minimum headland depth)
					for (const { pathResult, depth } of planPathResults) {
						if (pathResult) {
							const { newPlannedPath, newSimplePoints } =
								interpolatePlannedPath(pathResult.path);
							return {
								...result,
								version: pathResult.version,
								requestId: pathResult.requestId,
								pathResult: {
									path: newPlannedPath as LineString,
									estimatedCoverage: createEstimatedCoverage(
										newSimplePoints,
										sectionWidths,
									),
									validDepth: depth,
									pathPoints: newSimplePoints,
									preMissionMetrics: usePreMissionMetrics(
										fieldBoundary,
										pathResult.workAreas,
										newSimplePoints,
										targetSpeed,
										turningSpeed,
									),
									clothoid: pathResult.clothoid,
									mitigationData: pathResult.mitigationData,
								},
							};
						}
					}

					return result;
				} catch (error) {
					return result;
				}
			}),
		);

	const pathResults = pathResultsSettled
		.filter(result => result.status === "fulfilled")
		.map(result => result.value)
		.filter(result => result.pathResult !== undefined)
		// Limit number of corrected turns to a maximum of 10
		.filter(
			result =>
				(result.pathResult?.mitigationData?.correctedTurns?.length ??
					0) <= 10,
		) as PlanPathResult[];

	// Remove duplicate paths using hash-based deduplication
	const uniquePathResults = removeDuplicatePaths(pathResults);

	// Find the minimum headland depth among all valid results
	const minHeadlandDepth = Math.min(
		...uniquePathResults
			.map(result => result.pathResult?.validDepth)
			.filter((depth): depth is number => depth !== undefined),
	);

	// Calculate optimal metrics only for results with minimum headland depth
	let shortestLength = Infinity;
	let shortestTime = Infinity;
	let largestWorkArea = 0;
	let shortestTurnTime = Infinity;
	let highestEfficiency = 0;

	for (const result of uniquePathResults) {
		if (result.pathResult) {
			const currentLength =
				result.pathResult!.preMissionMetrics.pathLength;
			if (currentLength < shortestLength) {
				shortestLength = currentLength;
			}

			const currentTime = result.pathResult!.preMissionMetrics.totalTime;
			if (currentTime < shortestTime) {
				shortestTime = currentTime;
			}

			const currentWorkArea =
				result.pathResult!.preMissionMetrics.workAreaArea;
			if (currentWorkArea > largestWorkArea) {
				largestWorkArea = currentWorkArea;
			}

			const currentTurnTime =
				result.pathResult!.preMissionMetrics.turnTime;
			if (currentTurnTime < shortestTurnTime) {
				shortestTurnTime = currentTurnTime;
			}

			const currentEfficiency =
				result.pathResult!.preMissionMetrics.efficiencyRatio;
			if (currentEfficiency > highestEfficiency) {
				highestEfficiency = currentEfficiency;
			}
		}
	}

	// Set optimal flags only for results with minimum headland depth
	for (const result of uniquePathResults) {
		if (
			result.pathResult?.preMissionMetrics?.pathLength === shortestLength
		) {
			result.pathLengthOptimal = true;
		}
		if (result.pathResult?.preMissionMetrics?.totalTime === shortestTime) {
			result.totalTimeOptimal = true;
		}
		if (
			result.pathResult?.preMissionMetrics?.workAreaArea ===
			largestWorkArea
		) {
			result.workAreaAreaOptimal = true;
		}
		if (
			result.pathResult?.preMissionMetrics?.turnTime === shortestTurnTime
		) {
			result.turnTimeOptimal = true;
		}

		if (result.pathResult) {
			const currentEfficiency =
				result.pathResult.preMissionMetrics.efficiencyRatio;
			if (Math.abs(currentEfficiency - highestEfficiency) < 0.001) {
				// Use small epsilon for floating point comparison
				result.efficiencyOptimal = true;
			}
		}
	}

	// Set headlandDepthOptimal for all results with minimum depth
	for (const result of uniquePathResults) {
		if (result.pathResult?.validDepth === minHeadlandDepth) {
			result.headlandDepthOptimal = true;
		}
	}

	// Sort by minimum successful headland depth (validDepth), failed patterns last
	const sortedPathResults = uniquePathResults.toSorted((a, b) => {
		const aDepth = a.pathResult?.validDepth;
		const bDepth = b.pathResult?.validDepth;
		if (aDepth === undefined && bDepth === undefined) return 0;
		if (aDepth === undefined) return 1;
		if (bDepth === undefined) return -1;
		return aDepth - bDepth;
	});

	return sortedPathResults;
}
